import { useCallback, useState, type FormEvent } from 'react'
import { AlertTriangle, Loader2, Radar } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { login } from '@/lib/api'
import type { SessionInfo } from '@/types/liveview'

interface LoginScreenProps {
  onSignedIn: (session: SessionInfo) => void
  /** Why the page is here rather than signed in — a refused `?token=`. */
  initialError?: string | null
}

/**
 * Turns the error into the sentence MapTap would show for the same failure
 * (`maptap/src/components/login.tsx`). Two people who use both tools should not
 * have to learn two vocabularies for the same wrong password.
 */
function messageFor(error: unknown): string {
  if (error instanceof Error && error.name === 'UnauthorizedError') return 'Invalid credentials'
  const status = error instanceof Error ? Number(error.message.match(/^(\d{3})/)?.[1]) : NaN
  if (status === 429) return 'Too many attempts, please wait a moment and try again'
  if (status >= 500) return 'Server error, please try again later'
  if (Number.isNaN(status)) return 'Network error, check your connection'
  return 'Login failed'
}

export function LoginScreen({ onSignedIn, initialError = null }: LoginScreenProps) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(initialError)
  const [submitting, setSubmitting] = useState(false)

  const submit = useCallback(
    (event: FormEvent) => {
      event.preventDefault()
      if (submitting || !email.trim() || !password) return

      setSubmitting(true)
      setError(null)
      login(email.trim(), password)
        .then(onSignedIn)
        .catch((err: unknown) => {
          setError(messageFor(err))
          setPassword('')
          setSubmitting(false)
        })
    },
    [email, password, submitting, onSignedIn],
  )

  return (
    <div className="flex h-full items-center justify-center p-6">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Radar className="text-primary size-4" />
            <CardTitle>Proximi.io LiveView</CardTitle>
          </div>
          <CardDescription>
            Sign in with your Proximi.io account. You will see your organisation's venue and the
            devices reporting into it.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="flex flex-col gap-3" onSubmit={submit}>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium">Email</span>
              <Input
                type="email"
                autoComplete="username"
                autoFocus
                value={email}
                disabled={submitting}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>

            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium">Password</span>
              <Input
                type="password"
                autoComplete="current-password"
                value={password}
                disabled={submitting}
                onChange={(event) => setPassword(event.target.value)}
              />
            </label>

            {error && (
              <p
                role="alert"
                className="text-destructive flex items-center gap-1.5 text-xs font-medium"
              >
                <AlertTriangle className="size-3.5 shrink-0" />
                {error}
              </p>
            )}

            <Button type="submit" className="mt-1" disabled={submitting || !email.trim() || !password}>
              {submitting && <Loader2 className="animate-spin" />}
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
